import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useCaseDetailsContext from "../../hooks/useCaseDetailsContext";
import { getCaseSummaryPath } from "../../util/caseCategoryNavigation";
import Preloader from "../../Components/Preloader";
import "../../css/App.css";
import apiClient from "../../axios/apiClient";

const DynamicReport = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    pathname,
    actions,
    caseDetailsLists,
    dynamicReportStatus,
    loadingCaseDetails,
    clearDynamicReportType,
  } = useCaseDetailsContext();
  const [onlyOfficeScriptLoaded, setOnlyOfficeScriptLoaded] = useState(false);
  const [onlyOfficeError, setOnlyOfficeError] = useState("");
  const onlyOfficeEditorRef = useRef(null);

  const {
    AuthReducer: { auth, loading },
  } = useSelector((state) => state);

  useEffect(() => {
    if (id && auth.token) {
      dispatch(actions.getDynamicReportById(auth.token, id));
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    }
  }, [id, auth.token, pathname, dispatch, actions]);

  useEffect(() => {
    if (dynamicReportStatus?.success) {
      dispatch({ type: clearDynamicReportType });
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    }
  }, [dynamicReportStatus, dispatch, auth.token, id, pathname, actions, clearDynamicReportType]);

  const reportFormatUrl = caseDetailsLists?.reportFormatUrl || "";
  const showOnlyOfficeEditor = Boolean(reportFormatUrl);
  const onlyOfficeDocsUrl = (process.env.REACT_APP_ONLYOFFICE_DOCS_URL || "https://docs.asginnovations.in").replace(/\/+$/, "");

  useEffect(() => {
    if (!showOnlyOfficeEditor) return;
    setOnlyOfficeError("");
    if (window.DocsAPI?.DocEditor) {
      setOnlyOfficeScriptLoaded(true);
      return;
    }
    const scriptId = "onlyoffice-dynamic-report-api-script";
    const existingScript = document.getElementById(scriptId);
    if (existingScript) return;

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `${onlyOfficeDocsUrl}/web-apps/apps/api/documents/api.js`;
    script.async = true;
    script.onload = () => {
      setOnlyOfficeScriptLoaded(true);
      setOnlyOfficeError("");
    };
    script.onerror = () => {
      setOnlyOfficeError("Unable to load ONLYOFFICE script.");
      setOnlyOfficeScriptLoaded(false);
    };
    document.body.appendChild(script);
  }, [showOnlyOfficeEditor, onlyOfficeDocsUrl]);

  useEffect(() => {
    if (!showOnlyOfficeEditor || !onlyOfficeScriptLoaded) return;
    if (!window.DocsAPI?.DocEditor) return;

    let cancelled = false;
    const initOnlyOffice = async () => {
      try {
        const response = await apiClient.get(`/api/admin/v1/Onlyoffice-Dynamic-Report-Config/${id}`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        const configData = response?.data?.data;
        if (!configData || cancelled) return;

        if (onlyOfficeEditorRef.current?.destroyEditor) {
          onlyOfficeEditorRef.current.destroyEditor();
        }

        onlyOfficeEditorRef.current = new window.DocsAPI.DocEditor("onlyoffice-dynamic-report-editor", {
          ...configData,
          height: "100%",
          width: "100%",
          events: {
            onError: (event) => {
              const code = event?.data?.errorCode ?? event?.data?.error?.code;
              const description =
                event?.data?.errorDescription ||
                event?.data?.error?.description ||
                event?.data?.message ||
                "ONLYOFFICE editor error.";
              const message = code ? `ONLYOFFICE error ${code}: ${description}` : description;
              setOnlyOfficeError(message);
            },
          },
        });
      } catch (error) {
        if (cancelled) return;
        const msg = error?.response?.data?.message || "Unable to load ONLYOFFICE configuration.";
        setOnlyOfficeError(msg);
        toast.error(msg);
      }
    };

    initOnlyOffice();
    return () => {
      cancelled = true;
      if (onlyOfficeEditorRef.current?.destroyEditor) {
        onlyOfficeEditorRef.current.destroyEditor();
        onlyOfficeEditorRef.current = null;
      }
    };
  }, [showOnlyOfficeEditor, onlyOfficeScriptLoaded, auth.token, id]);

  if (loading || loadingCaseDetails) return <Preloader />;

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "Case Summary", path: getCaseSummaryPath(id, pathname) },
          { label: "Dynamic Report", path: pathname, active: true },
        ]}
        title={"Dynamic Report"}
        backButton={true}
        navigation={id ? getCaseSummaryPath(id, pathname) : "/Active-Case"}
      />
      <Row>
        <Col xs={12}>
          <Card>
            <Card.Body>
              {showOnlyOfficeEditor ? (
                <>
                  
                  {onlyOfficeError && <div className="mb-2 text-danger">{onlyOfficeError}</div>}
                  <div
                    style={{
                      border: "1px solid #dee2e6",
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "#fff",
                      height: "80vh",
                      minHeight: "1000px",
                      maxHeight: "calc(200px - 200px)",
                    }}
                  >
                    <div id="onlyoffice-dynamic-report-editor" style={{ width: "100%", height: "100%" }} />
                  </div>
                </>
              ) : (
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="text-muted">
                      Generated report DOCX not found. Trigger `Get-Dynamic-Report` API first to create it from template.
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default DynamicReport;
